import 'react-pdf/dist/esm/Page/AnnotationLayer.css'
import 'react-pdf/dist/esm/Page/TextLayer.css'

import { faFileDownload } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button, Modal } from '@orobix/orobix-fe-components'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { createUseStyles } from 'react-jss'
import { Document, Page, pdfjs } from 'react-pdf'

import {
  licenzaPdfFilename,
  licenzaPdfUrl as licenzaPDF,
} from '../../utils/licenzaPdf'
import useFooterStyles from './LicenseAgreementModal.styles'

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url
).toString()

const useStyles = createUseStyles({
  hideCloseButton: {
    '& [data-testid="modalCloseButton"]': {
      display: 'none',
    },
  },
  pdfContainer: {
    height: '500px',
    overflowY: 'auto',
    border: '1px solid #e0e0e0',
    borderRadius: '4px',
    '& .react-pdf__Document': {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    },
    '& .react-pdf__Page': {
      marginBottom: '8px',
    },
  },
  disabledHint: {
    position: 'relative',
    display: 'inline-flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
    '&:hover $disabledHintTooltip': {
      display: 'block',
    },
  },
  disabledHintTooltip: {
    display: 'none',
    position: 'absolute',
    bottom: 'calc(100% + 6px)',
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: '#333',
    color: '#fff',
    padding: '6px 10px',
    borderRadius: '4px',
    whiteSpace: 'nowrap',
    fontSize: '12px',
    pointerEvents: 'none',
    zIndex: 10,
  },
})

export type LicenseAgreementModalProps =
  | { onConfirm: () => void; onClose?: never }
  | { onClose: () => void; onConfirm?: never }

export const LicenseAgreementModal = ({
  onConfirm,
  onClose,
}: LicenseAgreementModalProps) => {
  const { t } = useTranslation()
  const [accepted, setAccepted] = useState(false)
  const [numPages, setNumPages] = useState<number | null>(null)
  const [lastPageReached, setLastPageReached] = useState(false)
  const classes = useStyles()
  const footerClasses = useFooterStyles()
  const viewOnly = onClose !== undefined
  const containerRef = useRef<HTMLDivElement | null>(null)

  const enableIfAtBottom = useCallback(() => {
    const container = containerRef.current
    if (!container) return
    const { scrollTop, scrollHeight, clientHeight } = container
    if (scrollTop + clientHeight >= scrollHeight - 20) {
      setLastPageReached(true)
    }
  }, [])

  useEffect(() => {
    const container = containerRef.current
    if (!container || viewOnly) return undefined
    container.addEventListener('scroll', enableIfAtBottom)
    return () => {
      container.removeEventListener('scroll', enableIfAtBottom)
    }
  }, [viewOnly, enableIfAtBottom])

  return (
    <div className={viewOnly ? undefined : classes.hideCloseButton}>
      <Modal
        size="xl"
        onCloseRequest={onClose ? () => onClose() : () => undefined}
        title={t('License terms of use')}
        footer={
          viewOnly ? (
            <div className={footerClasses.center}>
              <a
                href={licenzaPDF}
                download={licenzaPdfFilename}
                className={footerClasses.downloadLink}
              >
                <FontAwesomeIcon icon={faFileDownload} />
                {t('Download pdf license')}
              </a>
            </div>
          ) : (
            <div className={footerClasses.column}>
              <a
                href={licenzaPDF}
                download={licenzaPdfFilename}
                className={footerClasses.downloadLink}
              >
                <FontAwesomeIcon icon={faFileDownload} />
                {t('Download pdf license')}
              </a>
              <div className={classes.disabledHint}>
                {!lastPageReached && (
                  <div className={classes.disabledHintTooltip}>
                    {t(
                      'Read the entire document and accept the terms of use to continue'
                    )}
                  </div>
                )}
                <label
                  htmlFor="license-agreement-checkbox"
                  className={
                    lastPageReached
                      ? footerClasses.checkboxRow
                      : footerClasses.checkboxRowDisabled
                  }
                >
                  <input
                    id="license-agreement-checkbox"
                    type="checkbox"
                    checked={accepted}
                    disabled={!lastPageReached}
                    onChange={(e) => setAccepted(e.target.checked)}
                  />
                  {t('I have read and accepted the license terms of use')}
                </label>
                <Button
                  label={t('Confirm')}
                  disabled={!accepted}
                  onClick={onConfirm}
                  type="action"
                />
              </div>
            </div>
          )
        }
      >
        <div ref={containerRef} className={classes.pdfContainer}>
          <Document
            file={licenzaPDF}
            onLoadSuccess={({ numPages: n }) => setNumPages(n)}
          >
            {numPages !== null &&
              Array.from({ length: numPages }, (_, i) => {
                const pageNumber = i + 1
                const isLast = pageNumber === numPages
                return (
                  <div key={pageNumber}>
                    <Page
                      pageNumber={pageNumber}
                      width={740}
                      onRenderSuccess={isLast ? enableIfAtBottom : undefined}
                    />
                  </div>
                )
              })}
          </Document>
        </div>
      </Modal>
    </div>
  )
}

export default LicenseAgreementModal
